import { useEffect, useMemo } from "react";

import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Calendar,
  ClipboardCheck,
  ClipboardList,
  DollarSign,
  Link2,
  Star,
  X,
} from "lucide-react";

import { useAccountTiers } from "../../Utils/Helper/accountTierHelpers";
import { formatCompactCurrency } from "../../Utils/Helper/formatCompactCurrency";
import {
  formatAggregateRl,
  formatLevel,
  getAccountMappingDisplay,
  getInitials,
  getPrototypeAlBubbleClass,
  unassessedLevelTitle,
} from "../Relationship/account-relationships/relationshipUtils";
import { AccountBubbleDriftChevron } from "../Relationship/portfolio-overview/AccountBubbleDriftChevron";

type PreviewAccount = {
  id?: string;
  name?: string;
  alLevel?: number | null;
  rlAggregate?: number | null;
  tier?: string | null;
  cluster?: string | null;
  accountHealth?: number | null;
  businessHealth?: number | null;
  revenue?: number | null;
  renewalDate?: string | null;
  industry?: string | null;
  region?: string | null;
  mappedPairs?: number | null;
  assessedPairs?: number | null;
  pendingPairs?: number | null;
  pendingAssessments?: number | null;
  mappingPossiblePairs?: number | null;
  totalPossiblePairs?: number | null;
};

interface Props {
  open: boolean;
  account: PreviewAccount | null;
  onClose: () => void;
  onGoAccountSynopsis: () => void;
  onGoAccountRelationships: () => void;
  onGoMyRelationships: () => void;
  onOpenAssess?: () => void;
}

const formatRenewal = (raw?: string | null): string | null => {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const PortfolioMapAccountPreviewModal = ({
  open,
  account,
  onClose,
  onGoAccountSynopsis,
  onGoAccountRelationships,
  onGoMyRelationships,
  onOpenAssess,
}: Props) => {
  const { formatTier } = useAccountTiers();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const normalizedAccount = useMemo(() => {
    if (!account) return null;
    const pending = account.pendingAssessments ?? account.pendingPairs ?? null;
    const mappingPossible =
      account.mappingPossiblePairs ?? account.totalPossiblePairs ?? null;
    return {
      ...account,
      pendingAssessments: pending,
      mappingPossiblePairs: mappingPossible,
    };
  }, [account]);

  if (!open || !normalizedAccount) return null;

  const alLabel =
    normalizedAccount.alLevel === null ||
    normalizedAccount.alLevel === undefined
      ? "AL ?"
      : formatLevel("AL", normalizedAccount.alLevel);
  const rlLabel =
    normalizedAccount.rlAggregate === null ||
    normalizedAccount.rlAggregate === undefined
      ? "RL ?"
      : formatAggregateRl(normalizedAccount.rlAggregate);
  const mapping = getAccountMappingDisplay(normalizedAccount);
  const tierLabel =
    formatTier(normalizedAccount.tier) || normalizedAccount.tier || null;
  const industryLabel = normalizedAccount.industry?.trim() || null;
  const regionShort = normalizedAccount.region || null;

  const subtitleParts = [
    industryLabel,
    tierLabel || "Portfolio",
    regionShort,
  ].filter(Boolean);

  const ahValue =
    normalizedAccount.accountHealth != null &&
    normalizedAccount.accountHealth > 0
      ? Math.round(normalizedAccount.accountHealth)
      : null;
  const bhValue =
    normalizedAccount.businessHealth != null &&
    normalizedAccount.businessHealth > 0
      ? Math.round(normalizedAccount.businessHealth)
      : null;
  const renewalLabel = formatRenewal(normalizedAccount.renewalDate);
  const revenueLabel =
    normalizedAccount.revenue != null && normalizedAccount.revenue > 0
      ? formatCompactCurrency(normalizedAccount.revenue, { fallback: "—" })
      : null;

  const pendingRl =
    normalizedAccount.pendingAssessments != null
      ? Number(normalizedAccount.pendingAssessments) || 0
      : "—";

  const metaRows = [
    {
      icon: Building2,
      iconCls: "text-slate-500",
      label: "AH Score",
      value: ahValue != null ? String(ahValue) : "Unavailable",
      muted: ahValue == null,
    },
    {
      icon: Activity,
      iconCls: "text-emerald-600",
      label: "BH Score",
      value: bhValue != null ? String(bhValue) : "Unavailable",
      muted: bhValue == null,
    },
    revenueLabel
      ? {
          icon: DollarSign,
          iconCls: "text-amber-600",
          label: "Revenue",
          value: revenueLabel,
          muted: false,
        }
      : null,
    renewalLabel
      ? {
          icon: Calendar,
          iconCls: "text-blue-600",
          label: "Renewal",
          value: renewalLabel,
          muted: false,
        }
      : null,
  ].filter(Boolean) as Array<{
    icon: typeof Building2;
    iconCls: string;
    label: string;
    value: string;
    muted: boolean;
  }>;

  const actionItems = [
    {
      key: "synopsis",
      label: "Account Synopsis",
      icon: ClipboardList,
      iconCls: "text-gray-500",
      onClick: onGoAccountSynopsis,
    },
    {
      key: "relationships",
      label: "Account Relationships",
      icon: Link2,
      iconCls: "text-purple-600",
      onClick: onGoAccountRelationships,
    },
    ...(onOpenAssess
      ? [
          {
            key: "assess",
            label: "Assess",
            icon: ClipboardCheck,
            iconCls: "text-amber-600",
            onClick: onOpenAssess,
            // useRightArrow: true as const,
          },
        ]
      : []),
    {
      key: "my-relationships",
      label: "My Relationships",
      icon: Star,
      iconCls: "text-amber-500",
      onClick: onGoMyRelationships,
    },
  ];

  const actionBtn =
    "group flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-left text-[12.5px] font-semibold leading-snug text-gray-800 shadow-sm transition-colors hover:border-purple-400 hover:bg-purple-50 hover:text-purple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-1";

  return (
    <div
      id="modal-preview"
      className="modal-bg open !z-[1500]"
      onClick={onClose}
      role="presentation"
    >
      <article
        className="modal !max-w-[460px]"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ma-preview-title"
      >
        <header className="modal-hdr">
          <div
            className={`acct-bubble ${getPrototypeAlBubbleClass(normalizedAccount.alLevel)}`}
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              cursor: "default",
              borderWidth: 2,
            }}
            aria-hidden
          >
            <span className="ab-init" style={{ fontSize: 13 }}>
              {getInitials(normalizedAccount.name)}
            </span>
            <AccountBubbleDriftChevron
              rlAggregate={normalizedAccount.rlAggregate}
            />
          </div>
          <div className="mh-info">
            <h2 id="ma-preview-title" className="modal-title">
              {normalizedAccount.name || "Account"}
            </h2>
            <div className="modal-sub">{subtitleParts.join(" · ")}</div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2.25} />
          </button>
        </header>

        <div className="modal-body !pb-6">
          {metaRows.length > 0 ? (
            <div className="mt-0 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-3">
              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                {metaRows.map(row => {
                  const Icon = row.icon;
                  return (
                    <div
                      key={row.label}
                      className="flex min-w-0 items-center gap-2 text-[12.5px] text-gray-500"
                    >
                      <span
                        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center ${row.iconCls}`}
                      >
                        <Icon size={15} strokeWidth={2} />
                      </span>
                      <span className="min-w-0 truncate">
                        {row.label}:{" "}
                        <strong
                          className={
                            row.muted
                              ? "font-semibold italic text-gray-400"
                              : "font-semibold text-gray-800"
                          }
                        >
                          {row.value}
                        </strong>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="prev-stats !mt-3.5">
            <div className="prev-stat">
              <div className="prev-stat-lbl">Published AL</div>
              <div
                className="prev-stat-val"
                title={unassessedLevelTitle(alLabel)}
              >
                {alLabel}
              </div>
              <div className="prev-stat-sub">From Account Level</div>
            </div>
            <div className="prev-stat">
              <div className="prev-stat-lbl">Aggregate RL</div>
              <div
                className="prev-stat-val"
                title={unassessedLevelTitle(rlLabel)}
              >
                {rlLabel}
              </div>
              <div className="prev-stat-sub">Team lens (hybrid aggregate)</div>
            </div>
            <div className="prev-stat">
              <div className="prev-stat-lbl">Mapping</div>
              <div className="prev-stat-val">{mapping.ratioLabel}</div>
              <div className="prev-stat-sub">{mapping.subLabel}</div>
            </div>
            <div className="prev-stat">
              <div className="prev-stat-lbl">Pending RL</div>
              <div className="prev-stat-val">{pendingRl}</div>
              <div className="prev-stat-sub">Pairs to assess</div>
            </div>
          </div>

          <div className="prev-actions !mt-4 !grid !grid-cols-2 !gap-2.5">
            {actionItems.map(item => {
              const Icon = item.icon;
              const useRightArrow =
                "useRightArrow" in item && item.useRightArrow;
              const TrailingIcon = useRightArrow ? ArrowRight : ArrowUpRight;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={actionBtn}
                  onClick={item.onClick}
                >
                  <Icon size={15} className={`shrink-0 ${item.iconCls}`} />
                  <span className="min-w-0 flex-1">{item.label}</span>
                  <TrailingIcon
                    className="h-3.5 w-3.5 shrink-0 text-gray-400 transition-colors group-hover:text-purple-600"
                    strokeWidth={2.25}
                    aria-hidden
                  />
                </button>
              );
            })}
          </div>
        </div>
      </article>
    </div>
  );
};

export default PortfolioMapAccountPreviewModal;
